import { DOMAINS, FRAMEWORKS, REGULATORY_BASELINE } from "./library.js";
import { obligations } from "./engine.js";
import { FLAG_KEYS, JURISDICTIONS, SYSTEM_ROLES, US_STATES } from "./types.js";
const AI_TERMS = ["artificial intelligence", " ai ", "ai act", "automated decision", "algorithm", "machine learning"];
export const WATCH_SOURCES = [
    { id: "eurlex-ai-act", jurisdiction: "EU", kind: "page", title: "EUR-Lex: Regulation (EU) 2024/1689 (AI Act)",
        url: "https://eur-lex.europa.eu/eli/reg/2024/1689/oj", addedOn: "2026-10-17",
        obligations: ["eu-literacy", "eu-prohibited", "eu-gpai", "eu-interaction", "eu-marking", "eu-deepfake", "eu-hr-provider", "eu-hr-deployer", "eu-fria", "eu-annex-i"] },
    { id: "eurlex-omnibus", jurisdiction: "EU", kind: "page", title: "EUR-Lex: Regulation (EU) 2026/1744 (Digital Omnibus on AI)",
        url: "https://eur-lex.europa.eu/eli/reg/2026/1744/oj", addedOn: "2026-10-17",
        obligations: ["eu-literacy", "eu-prohibited", "eu-marking", "eu-hr-provider", "eu-hr-deployer", "eu-annex-i"] },
    { id: "ec-ai-act", jurisdiction: "EU", kind: "page", title: "European Commission: AI Act policy page",
        url: "https://digital-strategy.ec.europa.eu/en/policies/regulatory-framework-ai", addedOn: "2026-10-17",
        ignore: ["Latest News[\\s\\S]*$"],
        obligations: ["eu-literacy", "eu-prohibited", "eu-gpai", "eu-interaction", "eu-marking", "eu-hr-provider", "eu-hr-deployer", "eu-annex-i"] },
    { id: "govuk-dsit", jurisdiction: "UK", kind: "feed", title: "GOV.UK: Department for Science, Innovation and Technology activity",
        url: "https://www.gov.uk/government/organisations/department-for-science-innovation-and-technology.atom", addedOn: "2026-10-17",
        keywords: AI_TERMS, obligations: ["uk-principles", "uk-growth-bill", "uk-osa"] },
    { id: "legislation-duaa", jurisdiction: "UK", kind: "page", title: "legislation.gov.uk: Data (Use and Access) Act 2025",
        url: "https://www.legislation.gov.uk/ukpga/2025/18/contents", addedOn: "2026-10-17",
        obligations: ["uk-dp"] },
    { id: "industry-ai6", jurisdiction: "AU", kind: "page", title: "Department of Industry: Guidance for AI Adoption",
        url: "https://www.industry.gov.au/publications/guidance-for-ai-adoption", addedOn: "2026-10-17",
        obligations: ["au-ai6"] },
    { id: "dta-ai-policy", jurisdiction: "AU", kind: "page", title: "DTA: AI policy update for government",
        url: "https://www.dta.gov.au/articles/ai-policy-update-strengthening-responsible-use-across-government", addedOn: "2026-10-17",
        obligations: ["au-cth"] },
    { id: "federal-register-ai", jurisdiction: "US", kind: "feed", title: "Federal Register: documents mentioning artificial intelligence",
        url: "https://www.federalregister.gov/api/v1/documents.rss?conditions[term]=%22artificial+intelligence%22", addedOn: "2026-10-17",
        keywords: ["artificial intelligence", "automated decision", "algorithmic"],
        obligations: ["us-ftc", "us-civil-rights", "us-credit", "us-omb", "us-preemption"] },
    { id: "nist-ai-rmf", jurisdiction: "US", kind: "page", title: "NIST: AI Risk Management Framework",
        url: "https://www.nist.gov/itl/ai-risk-management-framework", addedOn: "2026-10-17",
        obligations: ["us-nist"], frameworks: ["NIST"] },
    { id: "cppa-regulations", jurisdiction: "US", kind: "page", title: "California Privacy Protection Agency: regulations",
        url: "https://cppa.ca.gov/regulations/", addedOn: "2026-10-17",
        obligations: ["ca-admt", "ca-risk"] },
    { id: "colorado-sb26-189", jurisdiction: "US", kind: "page", title: "Colorado General Assembly: SB26-189 Automated Decision-Making Technology",
        url: "https://leg.colorado.gov/bills/sb26-189", addedOn: "2026-10-17",
        obligations: ["co-admt-deployer", "co-admt-developer"] },
    { id: "nyc-ll144", jurisdiction: "US", kind: "page", title: "NYC DCWP: Automated employment decision tools (Local Law 144)",
        url: "https://www.nyc.gov/site/dca/about/automated-employment-decision-tools.page", addedOn: "2026-10-17",
        obligations: ["nyc-ll144"] },
    { id: "iso-42001", jurisdiction: "INTL", kind: "page", title: "ISO: ISO/IEC 42001 status",
        url: "https://www.iso.org/standard/42001", addedOn: "2026-10-17", obligations: [], frameworks: ["42001"] },
    { id: "iso-23894", jurisdiction: "INTL", kind: "page", title: "ISO: ISO/IEC 23894 status",
        url: "https://www.iso.org/standard/77304.html", addedOn: "2026-10-17", obligations: [], frameworks: ["23894"] },
    { id: "iso-42005", jurisdiction: "INTL", kind: "page", title: "ISO: ISO/IEC 42005 status",
        url: "https://www.iso.org/standard/42005", addedOn: "2026-10-17", obligations: [], frameworks: ["42005"] },
];
/**
 * When each obligation was last checked against its sources. Update the date
 * when you review an item, even if nothing changed.
 */
const REVIEWED_ON = {
    ...Object.fromEntries(["us-ftc", "us-civil-rights", "us-credit", "us-omb", "us-nist", "us-preemption", "ca-tfaia", "ca-caita", "ca-admt", "ca-risk",
        "ca-feha", "co-admt-deployer", "co-admt-developer", "il-hra", "il-frontier", "ny-raise", "nyc-ll144", "ct-aedt", "tx-traiga", "ut-disclosure", "au-cth"]
        .map((id) => [id, "2026-10-17"])),
};
const DEFAULT_REVIEWED_ON = "2026-10-13";
/**
 * Review interval: items still to be verified and watch items are re-checked
 * monthly; dated binding items quarterly; voluntary guidance every six months.
 */
export function reviewIntervalDays(o) {
    if (o.confidence === "M" || o.kind === "Watch")
        return 30;
    if (o.kind === "Voluntary")
        return 180;
    return 90;
}
function addDays(iso, days) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/** Every obligation the engine can produce, each once. */
export function allObligations() {
    const flags = Object.fromEntries(FLAG_KEYS.map((k) => [k, true]));
    const seen = new Map();
    for (const [domain] of DOMAINS)
        for (const role of SYSTEM_ROLES)
            for (const o of obligations({ role, domain, flags, jurisdictions: [...JURISDICTIONS], usStates: US_STATES.map((s) => s[0]) }))
                if (!seen.has(o.id))
                    seen.set(o.id, o);
    return [...seen.values()];
}
export function reviewSchedule(asOf) {
    return allObligations().map((o) => {
        const reviewedOn = REVIEWED_ON[o.id] ?? DEFAULT_REVIEWED_ON;
        const dueOn = addDays(reviewedOn, reviewIntervalDays(o));
        return { obligation: o, reviewedOn, dueOn, overdue: dueOn <= asOf, sources: WATCH_SOURCES.filter((s) => s.obligations.includes(o.id)).map((s) => s.id) };
    }).sort((a, b) => (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : a.obligation.id.localeCompare(b.obligation.id)));
}
export const WATCH_FRAMEWORK_KEYS = FRAMEWORKS.map((f) => f.key);
export const WATCH_BASELINE = REGULATORY_BASELINE;
//# sourceMappingURL=watch.js.map